import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#e8522a',
  'Product-Market Fit':          '#f4a261',
  'Early Growth & Monetization': '#4a7c3f',
  'Scaling & Regional Expansion':'#2a9d8f',
};

export default function CoachAssessmentsPage() {
  const { profile } = useAuth();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.uid) return;
    async function load() {
      try {
        const cSnap = await getDocs(query(collection(db, 'companies'), where('coachId', '==', profile.uid)));
        const result = [];
        for (const cDoc of cSnap.docs) {
          const comp = cDoc.data();
          const aSnap = await getDocs(collection(db, 'companies', cDoc.id, 'assessments'));
          const assessments = aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const da = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate);
              const db2 = b.assessmentDate?.toDate ? b.assessmentDate.toDate() : new Date(b.assessmentDate);
              return db2 - da;
            });
          result.push({ companyId: cDoc.id, companyName: comp.name, assessments });
        }
        setRows(result);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [profile]);

  const totalPending = rows.reduce((sum, r) =>
    sum + r.assessments.filter(a => a.status === 'submitted').length, 0);

  return (
    <DashboardLayout>
      <div className="gm-page">
        <div className="gm-page-header">
          <div>
            <h1 className="gm-page-title">Assessments</h1>
            <p className="gm-page-sub">
              Scaling assessments across your companies
              {totalPending > 0 && ` · ${totalPending} pending validation`}
            </p>
          </div>
        </div>

        {loading && <div className="gm-loading"><div className="gm-spinner" /></div>}

        {!loading && rows.map(({ companyId, companyName, assessments }) => (
          <div key={companyId} className="gm-section">
            <div className="gm-section-header">
              <h2 className="gm-section-title" style={{ marginBottom: 0 }}>
                <a href={`/scaling/coach/company/${companyId}`} style={{ color: 'inherit' }}>{companyName}</a>
              </h2>
              <a href={`/scaling/coach/company/${companyId}`} className="gm-btn gm-btn-sm gm-btn-outline">
                View company →
              </a>
            </div>

            <div className="gm-table-wrap">
              <table className="gm-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Stage</th>
                    <th>Progress in stage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map(a => {
                    const date = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate);
                    const color = STAGE_COLORS[a.stage] || '#4a5481';
                    const isPending = a.status === 'submitted';
                    return (
                      <tr key={a.id} style={{ background: isPending ? '#fff7ed' : undefined }}>
                        <td>
                          <span className={`gm-assessment-type-badge gm-assessment-type-badge--${a.type}`}>
                            {a.type === 'baseline' ? 'Baseline' : a.type === 'target' ? 'Target' : `Periodic${a.period ? ` · ${a.period}` : ''}`}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td>
                          <span style={{ color, fontWeight: 600, fontSize: '0.82rem' }}>{a.stage}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="gm-mini-progress" style={{ width: 80 }}>
                              <div className="gm-mini-progress-fill" style={{ width: `${a.stageProgress||0}%`, background: color }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{a.stageUpgrade}</span>
                          </div>
                        </td>
                        <td>
                          {isPending
                            ? <a href={`/scaling/coach/company/${companyId}`} className="gm-pending-badge" style={{ textDecoration: 'none' }}>🔔 Validate</a>
                            : <span className="gm-validated-badge">✓ {a.status}</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
